import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useBearerToken, useCdsid, useJwtRoles } from './AuthContext';
import { useGet } from '../hooks/useGet';
import RequestPermission from '../pages/RequestPermission/RequestPermission';

interface Permission {
  execute?: string[];
  read?: string[];
  write?: string[];
}

interface PermissionResponse {
  Permitted?: Permission[];
  user?: string;
}

interface RoleContextType {
  permissions: PermissionResponse;
  loading: boolean;
  hasPermission: (type: 'execute' | 'read' | 'write', role: string) => boolean;
}

export interface RequiredPermission {
  type: 'execute' | 'read' | 'write';
  role: string;
}

const RolesContext = createContext<RoleContextType | undefined>(undefined);

/**
 * CWE-639 FIX: Validate permissions response to detect tampering
 */
const validatePermissionsResponse = (
  response: any,
  expectedCdsId: string
): { valid: boolean; reason?: string } => {
  if (!response || !response.Permitted || !Array.isArray(response.Permitted)) {
    return { valid: false, reason: 'Invalid response structure - missing Permitted field' };
  }

  if (response.user && response.user.toUpperCase() !== expectedCdsId.toUpperCase()) {
    console.error(
      `SECURITY ALERT: Permissions response is for user ${response.user}, expected ${expectedCdsId}. ` +
      `This response may have been tampered with.`
    );
    return {
      valid: false,
      reason: `User mismatch: response is for ${response.user}, not ${expectedCdsId}`
    };
  }

  for (const perm of response.Permitted) {
    if (typeof perm !== 'object') {
      return { valid: false, reason: 'Permission entry is not an object' };
    }

    for (const key in perm) {
      if (!['execute', 'read', 'write'].includes(key)) {
        console.warn(`Unknown permission type: ${key}`);
      }

      if (Array.isArray(perm[key])) {
        if (!perm[key].every((item: any) => typeof item === 'string')) {
          return { valid: false, reason: `Permission ${key} contains invalid values` };
        }
      }
    }
  }

  return { valid: true };
};

/**
 * CWE-639 FIX: Detect suspiciously modified permission sets
 */
const isSuspiciousPermissionSet = (permissions: Permission[]): boolean => {
  for (const perm of permissions) {
    const hasExecute = perm.execute && perm.execute.length > 0;
    const hasWrite = perm.write && perm.write.length > 0;
    const hasRead = perm.read && perm.read.length > 0;

    if (hasExecute && hasWrite && hasRead && perm.execute && perm.execute.length > 3) {
      console.warn('SECURITY WARNING: Suspicious permission set detected');
      return true;
    }

    const dangerousPerms = ['DELETE_ALL', 'GRANT_ADMIN', 'GRANT_PERMISSIONS', 'ADMIN_DELETE'];
    const hasDangerous = perm.execute?.some(p => dangerousPerms.includes(p));
    
    if (hasDangerous) {
      console.warn('SECURITY WARNING: Suspicious permission set contains dangerous permissions');
      return true;
    }
  }

  return false;
};

export const RolesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {

  const [permissions, setPermissions] = useState<PermissionResponse>({});
  const [loading, setLoading] = useState(true);
  const bearerToken = useBearerToken();
  const cdsid = useCdsid();
  const { getData } = useGet();

  const isValidCdsid = (id: string): boolean => {
    if (!id) return false;
    return /^[a-zA-Z0-9]+$/.test(id);
  };

  useEffect(() => {
    const fetchAndStorePermissions = async () => {
      if (cdsid && bearerToken) {
        try {
          if (!isValidCdsid(cdsid)) {
            console.error(`Invalid cdsid format: ${cdsid}`);
            setPermissions({});
            setLoading(false);
            return;
          }

          const res = await getData(
            `${process.env.REACT_APP_FADSRS_API_URL}/mdp`
          );
          if (res) {
            // CWE-639 FIX: Validate response before storing permissions
            const validation = validatePermissionsResponse(res, cdsid);
            
            if (!validation.valid) {
              console.error(`SECURITY: Permission response validation failed: ${validation.reason}`);
              setPermissions({});
              setLoading(false);
              return;
            }

            // Additional check for suspicious permission sets
            if (isSuspiciousPermissionSet(res.Permitted || [])) {
              console.warn('SECURITY WARNING: Permissions appear suspicious');
            }

            setPermissions(res);
          } else {
            console.error('Failed to fetch permissions');
            setPermissions({});
          }
        } catch (error) {
          console.error('Error fetching permissions:', error);
          setPermissions({});
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(true);
      }
    };

    fetchAndStorePermissions();
  }, [cdsid, bearerToken, getData]);

  const hasPermission = (type: 'execute' | 'read' | 'write', role: string): boolean => {
    if (!permissions || !permissions.Permitted) return false;

    const requiredRoles = role.split('|');

    return permissions.Permitted.some(permission =>
        permission[type]?.some(userRole =>
            requiredRoles.includes(userRole)
        )
    );
  };

  return (
      <RolesContext.Provider value={{ permissions, loading, hasPermission }}>
        {children}
      </RolesContext.Provider>
  );
};

export const useRoles = (): RoleContextType => {
  const context = useContext(RolesContext);
  if (!context) {
    throw new Error('useRoles must be used within a RolesProvider');
  }
  return context;
};

// ─── Shared FSR Admin role list ────────────────────────────────────────────────
const FSR_ADMIN_ROLES = ['FSR:FSRAdministrator', 'FSR:customer'];

/**
 * Hook — returns true when the current user is allowed to access FSR Admin.
 *
 * Dual-gate logic:
 *   • If the Azure AD JWT token carries app-role claims → the user MUST hold
 *     one of FSR_ADMIN_ROLES in the token AND have execute permission in MDP.
 *   • If the JWT has no roles (unconfigured / legacy) → MDP execute-permission
 *     alone decides (backward-compatible fallback).
 */
export const useFsrAdminAccess = (): { hasAccess: boolean; loading: boolean } => {
  const { hasPermission, loading } = useRoles();
  const jwtRoles = useJwtRoles();

  const hasJwtRole: boolean | null =
    jwtRoles.length > 0
      ? FSR_ADMIN_ROLES.some(role => jwtRoles.includes(role))
      : null;

  const hasMdpPermission =
    hasPermission('execute', 'FSR:FSRAdministrator') ||
    hasPermission('execute', 'FSR:customer');

  const hasAccess =
    hasJwtRole !== null
      ? hasJwtRole && hasMdpPermission
      : hasMdpPermission;

  return { hasAccess, loading };
};

/**
 * FsrAdminGuard — wrap ANY FSR admin screen (or the whole FsrAdmin layout)
 * with this component.  If the user does not hold an FSR admin role they are
 * redirected to the RequestPermission page regardless of how they reached the
 * URL (direct navigation, back-button, link share, etc.).
 */
export const FsrAdminGuard: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { hasAccess, loading } = useFsrAdminAccess();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!hasAccess) {
    return <RequestPermission />;
  }

  return <>{children}</>;
};

interface ProtectedRouteProps {
  requiredPermission: RequiredPermission;
  element: React.ReactNode;
  fallback?: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredPermission,
  element,
  fallback = <RequestPermission />,
}) => {
  const { hasPermission, loading } = useRoles();
  // Pull JWT app-role claims from the Azure AD access token
  const jwtRoles = useJwtRoles();

  if (loading) {
    return <div>Loading...</div>;
  }

  const requiredRoleList = requiredPermission.role.split('|');

  // ── PRIMARY GATE: JWT roles ─────────────────────────────────────────────────
  // When the token carries app-role claims the user MUST hold one of the
  // required roles.  Null means "JWT check not applicable" (no roles in token).
  const hasJwtRole: boolean | null =
    jwtRoles.length > 0
      ? requiredRoleList.some(role => jwtRoles.includes(role))
      : null;

  // ── SECONDARY GATE: MDP permissions ─────────────────────────────────────────
  // SECURITY FIX: use ONLY the exact permission type specified by the caller.
  // The previous code checked execute/read/write regardless of `type`, allowing
  // users with only read/write MDP entries to bypass the execute gate.
  const hasMdpPermission = hasPermission(requiredPermission.type, requiredPermission.role);

  // ── ACCESS DECISION ─────────────────────────────────────────────────────────
  // JWT present → both must pass (defense-in-depth).
  // JWT absent  → MDP alone decides (backward-compatible).
  const hasAccess =
    hasJwtRole !== null
      ? hasJwtRole && hasMdpPermission
      : hasMdpPermission;

  if (!hasAccess) {
    return fallback as JSX.Element;
  }

  return element as JSX.Element;
};